import * as Sentry from '@sentry/vue';
import { uxrr } from '@zyno-io/uxrr-client';
import '@zyno-io/vue-foundation/dist/vue-foundation.css';
import { createPinia } from 'pinia';
import 'reflect-metadata';
import { createApp, watch } from 'vue';

import App from './app.vue';
import './otel';
import './shared/services/sse';
import { MobileBridge } from './modules/mobile/helper/bridge';
import { AuthBrowserApi } from './openapi-clients-generated/auth';
import { getRouteLocationIds, registerCurrentRouteIdsProvider } from './openapi-clients/helpers';
import { configureApiClients } from './openapi-clients/shared';
import { getSpanAttributes } from './otel';
import router from './router';
import { registerAnimationDirective } from './shared/directives/animation.directive';
import { registerClipTooltipDirective } from './shared/directives/clip-tooltip.directive';
import { registerNextFocusDirective } from './shared/directives/nextfocus.directive';
import { excludeChunkLoadErrors } from './shared/helpers/chunk-load-error';
import { installChunkLoadRecovery } from './shared/helpers/chunk-load.helpers';
import { IdleTimer } from './shared/helpers/idle.helpers';
import { excludePlayInterruptedErrors } from './shared/helpers/media.helpers';
import { hasPermission } from './shared/helpers/permissions.helpers';
import { initTheme, resolvedTheme } from './shared/services/theme.service';
import { setupVf } from './vf.setup';

// lazy (because it's a function call)
import('@fortawesome/fontawesome-pro/css/all.css');
import('@vuepic/vue-datepicker/dist/main.css');

configureApiClients();
registerCurrentRouteIdsProvider(() => getRouteLocationIds(router.currentRoute.value));

// Takes over `data-theme` from the pre-paint script in index.html and keeps it in sync with the
// stored preference / OS setting for the rest of the session.
initTheme();

// Push the active theme to the native shell so the status bar and safe-area background match;
// otherwise a dark app sits in a light native frame with a white band under the notch.
//
// Wired here rather than in theme.service.ts or app.mobile.ts, both of which are reachable from the
// embedded CX entry (cx.main → vf.setup → app.mobile). Importing MobileBridge from either drags the
// bridge — and its transitive weight — into the CX bundle, which
// src/embedded/cx/bundle-isolation.spec.ts rejects. main.ts is the full app's entry alone, and
// already imports the bridge above.
watch(
    resolvedTheme,
    theme => {
        if (!MobileBridge.isWithinZynoSuiteMobile) return;
        const styles = getComputedStyle(document.documentElement);
        const canvas = styles.getPropertyValue('--bg').trim();
        const chrome = styles.getPropertyValue('--chrome').trim();
        if (!canvas || !chrome) return;
        // A dark app needs light status-bar glyphs, and vice versa.
        void MobileBridge.sendRequest('setAppearance', {
            theme,
            style: theme === 'dark' ? 'light' : 'dark',
            canvas,
            chrome
        }).catch(() => undefined); // older native builds don't know this message
    },
    { immediate: true }
);

// When the user resumes activity after being idle, ping the server so it knows the session is live.
// Installed here (the full app) so the shared IdleTimer carries no dependency on the auth client.
IdleTimer.registerServerNotIdleHandler(() => void AuthBrowserApi.postBrowserDeidle());

// Stale tabs after a deploy: recover from lazy chunks that 404 outside wrapRuntimeImport.
installChunkLoadRecovery();

const app = createApp(App);

if (import.meta.env.VITE_APP_SENTRY_DSN) {
    const matches = import.meta.env.VITE_APP_SENTRY_DSN.match(/\/\/(.+)@.+\/(\d+)$/);
    if (matches) {
        Sentry.init({
            app: app,
            dsn: import.meta.env.VITE_APP_SENTRY_DSN,
            environment: import.meta.env.MODE,
            release: `zynosuite-spa@${import.meta.env.MODE}-${import.meta.env.VITE_APP_VERSION}`,
            tunnel: `https://sentry.s24.dev/proxy/${matches[2]}/${matches[1]}`,
            beforeSend: event => excludePlayInterruptedErrors(event) && excludeChunkLoadErrors(event),
            beforeBreadcrumb(breadcrumb) {
                const url = breadcrumb.category === 'fetch' ? breadcrumb.data?.url : null;
                return url?.includes('s24.dev') ? null : breadcrumb;
            }
        });
    }
}

// Unconditional on purpose. Without VITE_APP_UXRR_ENDPOINT uxrr ships nothing — no session, no logs,
// no spans — but it still installs OTel, so every API request carries a `traceparent` and the backend
// can correlate its trace with what the browser did. That makes trace IDs behave the same in dev,
// e2e, alpha and prod; only collection differs.
uxrr.init({
    endpoint: import.meta.env.VITE_APP_UXRR_ENDPOINT,
    appKey: import.meta.env.VITE_APP_UXRR_APP_KEY,
    version: import.meta.env.VITE_APP_VERSION,
    environment: import.meta.env.MODE,
    logging: {
        consolePrefix: 'ZS:'
    },
    tracing: {
        propagateToOrigins: [/localhost/, /zyno.app/, /zynobackup.net/, /^http:\/\/10\./],
        ignoreUrls: [/freshworks\.com/, /\.s24\.dev/, /version\.json/, /status\.zyno\.app/],
        spanAttributes: getSpanAttributes,
        includeRequestHeaders: 'always',
        includeResponseHeaders: 'always',
        includeRequestBody: 'onError'
    },
    recording: {
        privacy: {
            maskInputs: false,
            consoleLogLevel: ['debug', 'info', 'warn', 'error', 'log', 'assert']
        }
    }
});

setupVf(app, {
    $hp: hasPermission
});

const pinia = createPinia();
app.use(pinia);
app.use(router);

registerAnimationDirective(app);
registerNextFocusDirective(app);
registerClipTooltipDirective(app);

app.mount(document.body);

new Date(); // BS to force a new bundle hash to cache bust
